import { useEffect, useState } from 'react'
import { Alert, Button, Container, Form, Modal, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { getSuperAdminToken, removeSuperAdminAccess } from '../helpers'
import { t } from '../locale'

type Tenant = {
  dbName: string
  subdomain: string
  name: string | null
  mainUser: string
  isDemo: boolean
  piiEncryptionEnabled: boolean
}

type ConfirmAction = 'demo' | 'piiEncryption'

const CONFIRM_ACTION: Record<
  ConfirmAction,
  {
    endpoint: string
    bodyKey: string
    field: keyof Pick<Tenant, 'isDemo' | 'piiEncryptionEnabled'>
  }
> = {
  demo: { endpoint: 'demo', bodyKey: 'isDemo', field: 'isDemo' },
  piiEncryption: {
    endpoint: 'pii-encryption',
    bodyKey: 'piiEncryptionEnabled',
    field: 'piiEncryptionEnabled',
  },
}
type SuperAdmin = {
  _id: string
  username: string
  email: string
  createdAt: string
  setupToken?: string
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [admins, setAdmins] = useState<SuperAdmin[]>([])
  const [tenantsLoading, setTenantsLoading] = useState(false)
  const [adminsLoading, setAdminsLoading] = useState(false)
  const [tab, setTab] = useState<'tenants' | 'admins' | 'settings'>('tenants')
  const [settings, setSettings] = useState<{
    kycCheckEnabled: boolean
    amlSearchEnabled: boolean
    pinCodeCheckEnabled: boolean
    transparenzregisterChEnabled: boolean
    hubspotEnabled: boolean
    microsoftEnabled: boolean
    signupFormType: 'standard' | 'hubspot'
  }>({
    kycCheckEnabled: true,
    amlSearchEnabled: true,
    pinCodeCheckEnabled: true,
    transparenzregisterChEnabled: true,
    hubspotEnabled: true,
    microsoftEnabled: true,
    signupFormType: 'standard',
  })
  const [settingsLoading, setSettingsLoading] = useState(false)

  const [showAddModal, setShowAddModal] = useState(false)
  const [newUsername, setNewUsername] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [addError, setAddError] = useState('')
  const [addLoading, setAddLoading] = useState(false)

  const [confirmModal, setConfirmModal] = useState<{
    tenant: Tenant | null
    action: ConfirmAction
    pendingToken: string
    pinCode: string
    error: string
    loading: boolean
  }>({ tenant: null, action: 'demo', pendingToken: '', pinCode: '', error: '', loading: false })

  const loadTenants = async () => {
    setTenantsLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/tenants`)
      const body = await response.json()
      if (response.ok) setTenants(body.tenants ?? [])
    } finally {
      setTenantsLoading(false)
    }
  }

  const loadAdmins = async () => {
    setAdminsLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/admins`)
      const body = await response.json()
      if (response.ok) setAdmins(body.admins ?? [])
    } finally {
      setAdminsLoading(false)
    }
  }

  const loadSettings = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/global-settings`)
    if (response.ok) {
      const body = await response.json()
      setSettings({
        kycCheckEnabled: body.kycCheckEnabled,
        amlSearchEnabled: body.amlSearchEnabled,
        pinCodeCheckEnabled: body.pinCodeCheckEnabled,
        transparenzregisterChEnabled: body.transparenzregisterChEnabled,
        hubspotEnabled: body.hubspotEnabled,
        microsoftEnabled: body.microsoftEnabled,
        signupFormType: body.signupFormType === 'hubspot' ? 'hubspot' : 'standard',
      })
    }
  }

  const handleToggleSetting = async (
    key:
      | 'kycCheckEnabled'
      | 'amlSearchEnabled'
      | 'pinCodeCheckEnabled'
      | 'transparenzregisterChEnabled'
      | 'hubspotEnabled'
      | 'microsoftEnabled'
  ) => {
    setSettingsLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-super-admin-token': getSuperAdminToken() ?? '',
        },
        body: JSON.stringify({ [key]: !settings[key] }),
      })
      if (response.ok) setSettings(prev => ({ ...prev, [key]: !prev[key] }))
    } finally {
      setSettingsLoading(false)
    }
  }

  const handleSignupFormTypeChange = async (signupFormType: 'standard' | 'hubspot') => {
    setSettingsLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-super-admin-token': getSuperAdminToken() ?? '',
        },
        body: JSON.stringify({ signupFormType }),
      })
      if (response.ok) setSettings(prev => ({ ...prev, signupFormType }))
    } finally {
      setSettingsLoading(false)
    }
  }

  useEffect(() => {
    if (!getSuperAdminToken()) {
      navigate('/super-admin/login')
      return
    }
    loadTenants()
    loadAdmins()
    loadSettings()
  }, [])

  const openActionConfirm = async (tenant: Tenant, action: ConfirmAction) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/action-pin`, {
        method: 'POST',
      })
      const body = await response.json()
      if (!response.ok || !body?.pendingToken) return
      setConfirmModal({
        tenant,
        action,
        pendingToken: body.pendingToken,
        pinCode: '',
        error: '',
        loading: false,
      })
    } catch {
      // silently fail — user can retry
    }
  }

  const handleActionConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    const { tenant, action, pendingToken, pinCode } = confirmModal

    if (!tenant || !/^\d{6}$/.test(pinCode)) {
      setConfirmModal(prev => ({ ...prev, error: 'Please enter a valid 6-digit PIN code.' }))
      return
    }

    setConfirmModal(prev => ({ ...prev, loading: true, error: '' }))

    try {
      const verifyResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/super-admin/action-pin/verify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pendingToken, pinCode: Number(pinCode) }),
        }
      )
      const verifyBody = await verifyResponse.json()

      if (verifyBody?.err === 'pinCodeExpired') {
        setConfirmModal(prev => ({
          ...prev,
          loading: false,
          error: 'PIN code has expired. Please try again.',
        }))
        return
      }
      if (verifyBody?.err === 'invalidPinCode') {
        setConfirmModal(prev => ({ ...prev, loading: false, error: 'Invalid PIN code.' }))
        return
      }
      if (!verifyResponse.ok || !verifyBody?.ok) {
        setConfirmModal(prev => ({ ...prev, loading: false, error: 'Verification failed.' }))
        return
      }

      const { endpoint, bodyKey, field } = CONFIRM_ACTION[action]
      const nextValue = !tenant[field]

      const actionResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/super-admin/tenants/${tenant.subdomain}/${endpoint}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [bodyKey]: nextValue }),
        }
      )
      if (actionResponse.ok) {
        setTenants(prev =>
          prev.map(t => (t.subdomain === tenant.subdomain ? { ...t, [field]: nextValue } : t))
        )
      }

      setConfirmModal({
        tenant: null,
        action,
        pendingToken: '',
        pinCode: '',
        error: '',
        loading: false,
      })
    } catch {
      setConfirmModal(prev => ({ ...prev, loading: false, error: 'Network error.' }))
    }
  }

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    setAddError('')
    setAddLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/super-admin/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUsername, email: newEmail }),
      })
      const body = await response.json()
      if (!response.ok) {
        setAddError(body?.message?.content ?? 'Failed to add admin.')
        return
      }
      setShowAddModal(false)
      setNewUsername('')
      setNewEmail('')
      loadAdmins()
    } catch {
      setAddError('Network error.')
    } finally {
      setAddLoading(false)
    }
  }

  const handleDeleteAdmin = async (username: string) => {
    if (!window.confirm(`Delete admin "${username}"?`)) return
    await fetch(
      `${process.env.REACT_APP_API_URL}/super-admin/admins/${encodeURIComponent(username)}`,
      {
        method: 'DELETE',
      }
    )
    loadAdmins()
  }

  const logout = () => {
    removeSuperAdminAccess()
    navigate('/super-admin/login')
  }

  return (
    <Container className='py-4'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h4 className='mb-0'>Super Admin</h4>
        <Button variant='outline-secondary' size='sm' onClick={logout}>
          {t('logout')}
        </Button>
      </div>

      <div className='mb-3'>
        <Button
          variant={tab === 'tenants' ? 'primary' : 'outline-primary'}
          size='sm'
          className='me-2'
          onClick={() => setTab('tenants')}
        >
          Tenants
        </Button>
        <Button
          variant={tab === 'admins' ? 'primary' : 'outline-primary'}
          size='sm'
          className='me-2'
          onClick={() => setTab('admins')}
        >
          Admins
        </Button>
        <Button
          variant={tab === 'settings' ? 'primary' : 'outline-primary'}
          size='sm'
          onClick={() => setTab('settings')}
        >
          Settings
        </Button>
      </div>

      {tab === 'tenants' &&
        (tenantsLoading ? (
          <p>Loading...</p>
        ) : (
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>{t('database')}</th>
                <th>{t('subdomain')}</th>
                <th>{t('mainUser')}</th>
                <th>{t('companyName')}</th>
                <th>{t('demo')}</th>
                <th>{t('piiEncryption')}</th>
                <th>{t('action')}</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(te => (
                <tr key={te.dbName}>
                  <td>{te.dbName}</td>
                  <td>{te.subdomain}</td>
                  <td>{te.mainUser}</td>
                  <td>{te.name ?? '-'}</td>
                  <td>
                    <Form.Check checked={te.isDemo} readOnly />
                  </td>
                  <td>
                    <Form.Check checked={te.piiEncryptionEnabled} readOnly />
                  </td>
                  <td>
                    <Button
                      size='sm'
                      variant={te.isDemo ? 'warning' : 'success'}
                      className='me-2'
                      onClick={() => openActionConfirm(te, 'demo')}
                    >
                      {te.isDemo ? t('disableDemo') : t('enableDemo')}
                    </Button>
                    {te.piiEncryptionEnabled ? (
                      <Button size='sm' className='mt-2' variant='secondary' disabled>
                        {t('piiEncryptionPermanent')}
                      </Button>
                    ) : (
                      <Button
                        size='sm'
                        className='mt-2'
                        variant='success'
                        onClick={() => openActionConfirm(te, 'piiEncryption')}
                      >
                        {t('enablePiiEncryption')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ))}

      {tab === 'settings' && (
        <Table bordered>
          <thead>
            <tr>
              <th>Service</th>
              <th>Enabled</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>KYC Check (SCHUFA)</td>
              <td>
                <span className={`badge ${settings.kycCheckEnabled ? 'bg-success' : 'bg-danger'}`}>
                  {settings.kycCheckEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>
                <Button
                  size='sm'
                  variant={settings.kycCheckEnabled ? 'outline-danger' : 'outline-success'}
                  disabled={settingsLoading}
                  onClick={() => handleToggleSetting('kycCheckEnabled')}
                >
                  {settings.kycCheckEnabled ? 'Disable' : 'Enable'}
                </Button>
              </td>
            </tr>
            <tr>
              <td>AML Search</td>
              <td>
                <span className={`badge ${settings.amlSearchEnabled ? 'bg-success' : 'bg-danger'}`}>
                  {settings.amlSearchEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>
                <Button
                  size='sm'
                  variant={settings.amlSearchEnabled ? 'outline-danger' : 'outline-success'}
                  disabled={settingsLoading}
                  onClick={() => handleToggleSetting('amlSearchEnabled')}
                >
                  {settings.amlSearchEnabled ? 'Disable' : 'Enable'}
                </Button>
              </td>
            </tr>
            <tr>
              <td>Transparenzregister CH</td>
              <td>
                <span
                  className={`badge ${
                    settings.transparenzregisterChEnabled ? 'bg-success' : 'bg-danger'
                  }`}
                >
                  {settings.transparenzregisterChEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>
                <Button
                  size='sm'
                  variant={
                    settings.transparenzregisterChEnabled ? 'outline-danger' : 'outline-success'
                  }
                  disabled={settingsLoading}
                  onClick={() => handleToggleSetting('transparenzregisterChEnabled')}
                >
                  {settings.transparenzregisterChEnabled ? 'Disable' : 'Enable'}
                </Button>
              </td>
            </tr>
            <tr>
              <td>HubSpot account sync</td>
              <td>
                <span className={`badge ${settings.hubspotEnabled ? 'bg-success' : 'bg-danger'}`}>
                  {settings.hubspotEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>
                <Button
                  size='sm'
                  variant={settings.hubspotEnabled ? 'outline-danger' : 'outline-success'}
                  disabled={settingsLoading}
                  onClick={() => handleToggleSetting('hubspotEnabled')}
                >
                  {settings.hubspotEnabled ? 'Disable' : 'Enable'}
                </Button>
              </td>
            </tr>
            <tr>
              <td>Microsoft integration (SSO + SharePoint)</td>
              <td>
                <span className={`badge ${settings.microsoftEnabled ? 'bg-success' : 'bg-danger'}`}>
                  {settings.microsoftEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>
                <Button
                  size='sm'
                  variant={settings.microsoftEnabled ? 'outline-danger' : 'outline-success'}
                  disabled={settingsLoading}
                  onClick={() => handleToggleSetting('microsoftEnabled')}
                >
                  {settings.microsoftEnabled ? 'Disable' : 'Enable'}
                </Button>
              </td>
            </tr>

            <tr>
              <td>Require PIN code for user actions</td>
              <td>
                <span
                  className={`badge ${settings.pinCodeCheckEnabled ? 'bg-success' : 'bg-danger'}`}
                >
                  {settings.pinCodeCheckEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>
                <Button
                  size='sm'
                  variant={settings.pinCodeCheckEnabled ? 'outline-danger' : 'outline-success'}
                  disabled={settingsLoading}
                  onClick={() => handleToggleSetting('pinCodeCheckEnabled')}
                >
                  {settings.pinCodeCheckEnabled ? 'Disable' : 'Enable'}
                </Button>
              </td>
            </tr>
            <tr>
              <td>Signup Form (/company/create)</td>
              <td>
                <span className='badge bg-secondary'>
                  {settings.signupFormType === 'hubspot' ? 'HubSpot' : 'Standard'}
                </span>
              </td>
              <td>
                <Form.Select
                  size='sm'
                  style={{ maxWidth: 180 }}
                  value={settings.signupFormType}
                  disabled={settingsLoading}
                  onChange={e =>
                    handleSignupFormTypeChange(e.target.value as 'standard' | 'hubspot')
                  }
                >
                  <option value='standard'>Standard</option>
                  <option value='hubspot'>HubSpot</option>
                </Form.Select>
              </td>
            </tr>
          </tbody>
        </Table>
      )}

      {tab === 'admins' && (
        <>
          <div className='d-flex justify-content-end mb-2'>
            <Button
              size='sm'
              variant='primary'
              onClick={() => {
                setAddError('')
                setShowAddModal(true)
              }}
            >
              + Add Admin
            </Button>
          </div>

          {adminsLoading ? (
            <p>Loading...</p>
          ) : (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a._id}>
                    <td>{a.username}</td>
                    <td>{a.email}</td>
                    <td>
                      <span
                        className={`badge ${a.setupToken ? 'bg-warning text-dark' : 'bg-success'}`}
                      >
                        {a.setupToken ? 'Pending setup' : 'Active'}
                      </span>
                    </td>
                    <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                    <td>
                      <Button
                        size='sm'
                        variant='outline-danger'
                        onClick={() => handleDeleteAdmin(a.username)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      <Modal
        show={!!confirmModal.tenant}
        onHide={() =>
          setConfirmModal({
            tenant: null,
            action: 'demo',
            pendingToken: '',
            pinCode: '',
            error: '',
            loading: false,
          })
        }
      >
        <Form onSubmit={handleActionConfirm}>
          <Modal.Header closeButton>
            <h4 className='modal-title'>{t('twoFactorAuth')}</h4>
          </Modal.Header>
          <Modal.Body>
            <p className='mb-3'>
              {t('pinCodeSent')}
              {' \n'}
              <strong>
                {confirmModal.action === 'demo'
                  ? `${confirmModal.tenant?.isDemo ? t('disable') : t('enable')} demo`
                  : `${confirmModal.tenant?.piiEncryptionEnabled ? t('disable') : t('enable')} ${t('piiEncryption')}`}
              </strong>{' '}
              {t('for')}{' '}
              <strong>{confirmModal.tenant?.name ?? confirmModal.tenant?.subdomain}</strong>.
            </p>
            <Form.Group>
              <Form.Label>PIN Code</Form.Label>
              <Form.Control
                type='text'
                inputMode='numeric'
                maxLength={6}
                value={confirmModal.pinCode}
                onChange={e =>
                  setConfirmModal(prev => ({
                    ...prev,
                    pinCode: e.target.value.replace(/\D/g, ''),
                  }))
                }
                autoComplete='one-time-code'
                autoFocus
                placeholder='6-digit code'
              />
            </Form.Group>
            {!!confirmModal.error && (
              <Alert variant='danger' className='mt-3 mb-0'>
                {confirmModal.error}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant='secondary'
              onClick={() =>
                setConfirmModal({
                  tenant: null,
                  action: 'demo',
                  pendingToken: '',
                  pinCode: '',
                  error: '',
                  loading: false,
                })
              }
            >
              {t('cancel')}
            </Button>
            <Button type='submit' variant='primary' disabled={confirmModal.loading}>
              {confirmModal.loading ? 'Verifying...' : t('confirm')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Form onSubmit={handleAddAdmin}>
          <Modal.Header closeButton>
            <Modal.Title>Add Super Admin</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className='mb-3'>
              <Form.Label>Username</Form.Label>
              <Form.Control
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type='email'
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                required
              />
            </Form.Group>
            {!!addError && <Alert variant='danger'>{addError}</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button type='submit' variant='primary' disabled={addLoading}>
              {addLoading ? 'Sending...' : 'Send Invite'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}
